import { Theme } from '@mui/joy';
import dayjs, { Dayjs } from 'dayjs';

import { DB_DATE_FORMAT } from 'core/constants';
import { ITimeLog } from 'jobs/components/JobDetails';

import { ITimeLogCalendarDay } from './types';

export const getTimeLogCalendarDays = (
  currentDate: Dayjs,
  timeLogs: ITimeLog[]
): ITimeLogCalendarDay[] => {
  const year = currentDate.year();
  const month = currentDate.month();

  const firstDay = dayjs().year(year).month(month).date(1);
  const lastDay = firstDay.endOf('month');

  const startDay = firstDay.startOf('week');
  const endDay = lastDay.endOf('week');

  const days = [];
  let currentDay = startDay;

  // Calculate total days needed to fill the calendar grid
  const totalDays = endDay.diff(startDay, 'day') + 1;

  const timeDataMap = getTimeDataMap(timeLogs);

  for (let i = 0; i < totalDays; i++) {
    const isCurrentMonth = currentDay.month() === month;

    const dateKey = currentDay.format(DB_DATE_FORMAT);

    const hours = timeDataMap?.[dateKey] || 0;

    days.push({
      date: currentDay,
      day: currentDay.date(),
      isCurrentMonth,
      hours,
      dateKey,
      isToday: currentDay.isSame(dayjs(), 'day'),
    });

    currentDay = currentDay.add(1, 'day');
  }

  return days;
};

export const getDayColor = (theme: Theme, day: ITimeLogCalendarDay, isSelected: boolean) => {
  if (day.isToday) {
    return theme.palette.common.white;
  }

  if (!day.isCurrentMonth) {
    return theme.palette.text.tertiary;
  }

  if (isSelected) {
    return theme.palette.primary[600];
  }

  return theme.palette.text.primary;
};

export const getDayBackgroundColor = (
  theme: Theme,
  isIntervalStart: boolean,
  isIntervalEnd: boolean,
  isInInterval: boolean,
  isInPreview = false
): string => {
  if (isIntervalStart || isIntervalEnd) {
    return theme.palette.primary[200];
  }
  if (isInInterval) {
    return theme.palette.primary[100];
  }
  if (isInPreview) {
    return theme.palette.primary[50];
  }

  return theme.palette.background.surface;
};

export const getHoverBackgroundColor = (theme: Theme, isInInterval: boolean): string => {
  if (isInInterval) {
    return theme.palette.primary[200];
  }

  return theme.palette.primary[50];
};

const getTimeDataMap = (timeLogs: ITimeLog[]) =>
  timeLogs?.reduce(
    (map, field: ITimeLog) => {
      const dateKey = dayjs(field.activityDate).format(DB_DATE_FORMAT);
      map[dateKey] = (map[dateKey] || 0) + field.duration;

      return map;
    },
    {} as Record<string, number>
  );
